import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import MgmtLayout from '@/components/feature/MgmtLayout';
import NewChangeOrderModal from '@/components/feature/NewChangeOrderModal';
import EditChangeOrderModal from '@/components/feature/EditChangeOrderModal';
import { formatClientFacingPrice, formatInternalPricing } from '@/lib/changeOrderPricingDisplay';
import {
  buildLockedCommercialUpdate,
  inferChangeOrderPricingType,
  isLockedChangeOrderStatus,
  persistFormPricing,
  formatSignedCurrency,
} from '@/lib/changeOrderPricing';
import InstructionAttachmentThumbnails, { type InstructionAttachment } from '@/components/feature/InstructionAttachmentThumbnails';
import ChangeOrderAttachmentList from '@/components/feature/ChangeOrderAttachmentList';
import FileUploader from '@/components/feature/FileUploader';
import { supabase } from '@/lib/supabase';
import { formatDateTime } from '@/lib/dateUtils';
import { useAuth } from '@/hooks/useAuth';
import CoNumberQuickEditModal from '@/components/feature/CoNumberQuickEditModal';
import {
  normalizeCoNumber,
  resolveCoNumberFields,
  validateCoNumberUnique,
  deleteChangeOrder,
  unlinkChangeOrderAttachment,
  buildChangeOrderClientVisibilityPatch,
  getChangeOrderClientPortalState,
  isChangeOrderArchived,
  isApprovedChangeOrder,
  isChangeOrderNoPricing,
  requiresDeleteStrongWarning,
  updateChangeOrderCoNumber,
  CHANGE_ORDERS_SYNC_EVENT,
  emitChangeOrdersSync,
} from '@/lib/changeOrderUtils';

interface ChangeOrderRecord {
  id: string;
  project_id: string;
  instruction_id: string | null;
  title: string;
  description: string | null;
  client_description: string | null;
  reason: string | null;
  status: string;
  requires_client_approval: boolean;
  client_decision: string | null;
  client_comment: string | null;
  client_decided_at: string | null;
  cost_impact: string | null;
  schedule_impact: string | null;
  current_finish_description: string | null;
  new_finish_description: string | null;
  co_number: string | null;
  co_sequence: number | null;
  builder_cost: number | null;
  client_price: number | null;
  internal_note: string | null;
  client_visible: boolean | null;
  invoice_status: string | null;
  invoice_url: string | null;
  invoice_file_id: string | null;
  invoiced_at: string | null;
  paid_at: string | null;
  created_at: string;
  updated_at: string;
  instruction_to_user_id?: string | null;
}

interface ProjectRecord {
  id: string;
  project_name: string;
  project_code: string;
  client_name?: string | null;
}

interface SubcontractorProfile {
  id: string;
  full_name: string | null;
  company_name: string | null;
}

const CO_STATUS_STYLES: Record<string, { bg: string; text: string; label: string }> = {
  draft:                    { bg: '#F3F4F6', text: '#6B7280', label: 'Draft' },
  pending_client_approval:  { bg: '#FEF0E7', text: '#E8621A', label: 'Pending Approval' },
  sent_to_client:           { bg: '#FEF0E7', text: '#E8621A', label: 'Pending Approval' },
  approved:                 { bg: '#F0FDF4', text: '#059669', label: 'Approved' },
  client_approved:          { bg: '#F0FDF4', text: '#059669', label: 'Approved' },
  rejected:                 { bg: '#FEF2F2', text: '#EF4444', label: 'Rejected' },
  client_rejected:          { bg: '#FEF2F2', text: '#EF4444', label: 'Rejected' },
  closed:                   { bg: '#F9FAFB', text: '#9CA3AF', label: 'Closed' },
  in_progress:              { bg: '#FFFBEB', text: '#F59E0B', label: 'In Progress' },
  completed:                { bg: '#F0FDF4', text: '#059669', label: 'Completed' },
  cancelled:                { bg: '#F9FAFB', text: '#9CA3AF', label: 'Cancelled' },
};

function isPending(status: string): boolean {
  return ['pending_client_approval', 'sent_to_client'].includes(status);
}

function getStatusStyle(status: string) {
  return CO_STATUS_STYLES[status] || CO_STATUS_STYLES.draft;
}

function mapChangeOrderAttachmentRow(row: Record<string, unknown>): InstructionAttachment {
  return {
    id: row.id as string,
    file_url: row.file_url as string,
    file_name: row.file_name as string,
    file_type: row.file_type as string | null,
    file_size: row.file_size as string | number | null,
    photo_type: row.photo_type as string | null,
    caption: (row.caption as string | null) ?? (row.note as string | null) ?? null,
    note: row.note as string | null,
    is_client_visible: row.is_client_visible as boolean | null,
    change_order_id: row.change_order_id as string | null,
    created_at: row.created_at as string | null,
    storage_bucket: row.storage_bucket as string | null ?? null,
    storage_path: row.storage_path as string | null ?? null,
  };
}

const EDITABLE_STATUSES_LABELS: Record<string, string> = {
  draft: 'Draft',
  pending_client_approval: 'Pending Client Approval',
  approved: 'Approved',
  rejected: 'Rejected',
  closed: 'Closed',
};

type ViewMode = 'table' | 'cards';

export default function ManagementChangeOrdersPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();

  const [projects, setProjects] = useState<ProjectRecord[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState('all');
  const [changeOrders, setChangeOrders] = useState<ChangeOrderRecord[]>([]);
  const [subcontractors, setSubcontractors] = useState<SubcontractorProfile[]>([]);
  const [attachments, setAttachments] = useState<Record<string, InstructionAttachment[]>>({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const hasLoadedOnceRef = useRef(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editCo, setEditCo] = useState<ChangeOrderRecord | null>(null);
  const [invoiceFieldsAvailable, setInvoiceFieldsAvailable] = useState(true);
  const [viewMode, setViewMode] = useState<ViewMode>('table');

  // Detail drawer
  const [drawerCo, setDrawerCo] = useState<ChangeOrderRecord | null>(null);

  // Edit state
  const [editingId, setEditingId] = useState<string | null>(null);
  const emptyForm = {
    co_number: '',
    title: '',
    description: '',
    client_description: '',
    reason: '',
    current_finish_description: '',
    new_finish_description: '',
    schedule_impact: '',
    builder_cost: '',
    client_price: '',
    internal_note: '',
    status: 'draft',
    requires_client_approval: true,
  };
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [coNumberEditCo, setCoNumberEditCo] = useState<ChangeOrderRecord | null>(null);
  const [coNumberEditError, setCoNumberEditError] = useState<string | null>(null);
  const [coNumberSaving, setCoNumberSaving] = useState(false);
  const [actionMessage, setActionMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [showFileUploader, setShowFileUploader] = useState(false);
  const [attachmentLoadingIds, setAttachmentLoadingIds] = useState<Set<string>>(new Set());
  const [attachmentErrors, setAttachmentErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    supabase
      .from('jphph_projects')
      .select('id, project_name, project_code, client_name')
      .order('project_name', { ascending: true })
      .then(({ data }) => setProjects((data || []) as ProjectRecord[]));
  }, []);

  const loadAttachmentsForChangeOrder = useCallback(async (changeOrderId: string) => {
    setAttachmentLoadingIds((prev) => new Set(prev).add(changeOrderId));
    setAttachmentErrors((prev) => {
      const next = { ...prev };
      delete next[changeOrderId];
      return next;
    });

    try {
      const { data, error } = await supabase
        .from('jphph_project_files')
        .select('id, file_name, file_url, file_type, file_size, photo_type, caption, note, change_order_id, is_client_visible, created_at, storage_bucket, storage_path')
        .eq('change_order_id', changeOrderId)
        .eq('is_deleted', false)
        .order('created_at', { ascending: true });

      if (error) throw error;

      const mapped = ((data || []) as Record<string, unknown>[])
        .filter((row) => row.photo_type !== 'co_invoice')
        .map(mapChangeOrderAttachmentRow);
      console.warn('[ChangeOrders] fetched edit attachments', {
        change_order_id: changeOrderId,
        count: mapped.length,
      });
      setAttachments((prev) => ({ ...prev, [changeOrderId]: mapped }));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load attachments';
      console.warn('[ChangeOrders] failed to load edit attachments', {
        change_order_id: changeOrderId,
        error: message,
      });
      setAttachmentErrors((prev) => ({ ...prev, [changeOrderId]: message }));
    } finally {
      setAttachmentLoadingIds((prev) => {
        const next = new Set(prev);
        next.delete(changeOrderId);
        return next;
      });
    }
  }, []);

  const load = useCallback(async () => {
    const isFirstLoad = !hasLoadedOnceRef.current;
    if (isFirstLoad) setLoading(true);
    else setRefreshing(true);

    try {
      const selectWithInvoice =
        'id, project_id, instruction_id, title, description, client_description, reason, status, requires_client_approval, client_decision, client_comment, client_decided_at, cost_impact, schedule_impact, current_finish_description, new_finish_description, co_number, co_sequence, builder_cost, client_price, internal_note, client_visible, invoice_status, invoice_url, invoice_file_id, invoiced_at, paid_at, created_at, updated_at';
      const selectBase =
        'id, project_id, instruction_id, title, description, client_description, reason, status, requires_client_approval, client_decision, client_comment, client_decided_at, cost_impact, schedule_impact, current_finish_description, new_finish_description, co_number, co_sequence, builder_cost, client_price, internal_note, client_visible, created_at, updated_at';

      let query = supabase
        .from('jphph_change_orders')
        .select(selectWithInvoice)
        .order('co_sequence', { ascending: true, nullsFirst: false });

      if (selectedProjectId !== 'all') {
        query = query.eq('project_id', selectedProjectId);
      }

      let coResult = await query;
      if (coResult.error && /invoice|schema cache|column/i.test(coResult.error.message)) {
        setInvoiceFieldsAvailable(false);
        let fallback = supabase
          .from('jphph_change_orders')
          .select(selectBase)
          .order('co_sequence', { ascending: true, nullsFirst: false });
        if (selectedProjectId !== 'all') {
          fallback = fallback.eq('project_id', selectedProjectId);
        }
        coResult = (await fallback) as typeof coResult;
      } else {
        setInvoiceFieldsAvailable(true);
      }

    const { data: coData } = coResult;
    let cos = (coData || []) as ChangeOrderRecord[];
    cos = Array.from(new Map(cos.map((co) => [co.id, co])).values());

    const instrIds = cos.map((c) => c.instruction_id).filter(Boolean) as string[];
    if (instrIds.length > 0) {
      const { data: instrData } = await supabase
        .from('jphph_instructions')
        .select('id, to_user_id')
        .in('id', instrIds);
      const instrMap: Record<string, string | null> = {};
      (instrData || []).forEach((i) => { instrMap[i.id] = i.to_user_id; });
      cos.forEach((co) => {
        if (co.instruction_id) co.instruction_to_user_id = instrMap[co.instruction_id] || null;
      });
    }

    setChangeOrders(cos);

    const toUserIds = [...new Set(cos.map((c) => c.instruction_to_user_id).filter(Boolean))] as string[];
    if (toUserIds.length > 0) {
      const { data: profs } = await supabase
        .from('jphph_profiles')
        .select('id, full_name, company_name')
        .in('id', toUserIds);
      setSubcontractors((profs || []) as SubcontractorProfile[]);
    }

    if (cos.length > 0) {
      const coIds = cos.map((c) => c.id);
      const { data: filesData, error: filesError } = await supabase
        .from('jphph_project_files')
        .select('id, file_name, file_url, file_type, file_size, photo_type, caption, note, change_order_id, is_client_visible, created_at, storage_bucket, storage_path')
        .or(coIds.map((id) => `change_order_id.eq.${id}`).join(','))
        .eq('is_deleted', false);

      if (filesError) {
        console.warn('[ChangeOrders] failed to load attachment summaries', {
          change_order_ids: coIds,
          error: filesError.message,
        });
        setAttachmentErrors((prev) => {
          const next = { ...prev };
          coIds.forEach((id) => { next[id] = filesError.message; });
          return next;
        });
      } else {
        const grouped: Record<string, InstructionAttachment[]> = {};
        ((filesData || []) as Record<string, unknown>[]).forEach((f) => {
          if (f.photo_type === 'co_invoice') return;
          const key = f.change_order_id as string || '';
          if (!grouped[key]) grouped[key] = [];
          grouped[key].push(mapChangeOrderAttachmentRow(f));
        });
        setAttachments(grouped);
        setAttachmentErrors((prev) => {
          const next = { ...prev };
          coIds.forEach((id) => { delete next[id]; });
          return next;
        });
      }
    } else {
      setAttachments({});
    }
    hasLoadedOnceRef.current = true;
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [selectedProjectId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ projectId?: string }>).detail;
      if (detail?.projectId && selectedProjectId !== 'all' && detail.projectId !== selectedProjectId) {
        return;
      }
      void load();
    };
    window.addEventListener(CHANGE_ORDERS_SYNC_EVENT, handler);
    return () => window.removeEventListener(CHANGE_ORDERS_SYNC_EVENT, handler);
  }, [load, selectedProjectId]);

  function openEdit(co: ChangeOrderRecord) {
    if (isChangeOrderArchived(co.status)) return;
    // Canonical editor for all statuses — Client Price remains editable even when approved.
    setEditCo(co);
    setShowEditModal(true);
    setDrawerCo(null);
    setEditingId(null);
    void loadAttachmentsForChangeOrder(co.id);
  }

  function cancelEdit() {
    setEditingId(null);
    setForm(emptyForm);
    setFormError(null);
    setShowFileUploader(false);
  }

  async function handleDeleteChangeOrder(co: ChangeOrderRecord) {
    const result = await deleteChangeOrder(co);
    if (result.error) {
      setActionMessage({ type: 'error', text: result.error });
      return;
    }
    setDrawerCo(null);
    setConfirmDeleteId(null);
    setActionMessage({ type: 'success', text: t('co.deletedMessage') });
    await load();
  }

  async function handleSaveCoNumber(coNumber: string) {
    if (!coNumberEditCo) return;
    setCoNumberSaving(true);
    setCoNumberEditError(null);
    try {
      const result = await updateChangeOrderCoNumber({
        id: coNumberEditCo.id,
        projectId: coNumberEditCo.project_id,
        coNumberInput: coNumber,
        previousCoNumber: coNumberEditCo.co_number,
        userId: user?.id ?? null,
      });
      if (result.error) {
        setCoNumberEditError(result.error);
        return;
      }
      setCoNumberEditCo(null);
      setActionMessage({ type: 'success', text: t('co.coNumberUpdated', { number: result.co_number }) });
      await load();
    } finally {
      setCoNumberSaving(false);
    }
  }

  async function handleSaveEdit() {
    if (!editingId || !form.title.trim() || !form.co_number.trim()) return;
    setSaving(true);
    setFormError(null);
    try {
      const co = changeOrders.find((c) => c.id === editingId);
      if (!co) return;
      const builderNum = form.builder_cost.trim() ? Number(form.builder_cost) : null;
      const clientNum = form.client_price.trim() ? Number(form.client_price) : null;
      const pricingType = inferChangeOrderPricingType(
        Number.isFinite(builderNum as number) ? builderNum : null,
        Number.isFinite(clientNum as number) ? clientNum : null,
        { title: form.title, reason: form.reason }
      );
      const pricingResult = persistFormPricing(
        builderNum != null && Number.isFinite(builderNum) ? String(Math.abs(builderNum)) : form.builder_cost,
        clientNum != null && Number.isFinite(clientNum) ? String(Math.abs(clientNum)) : form.client_price,
        pricingType
      );
      if (pricingResult.error) {
        setFormError(pricingResult.error);
        return;
      }

      const commercial = {
        builder_cost: pricingResult.pricing.builder_cost,
        client_price: pricingResult.pricing.client_price,
        internal_note: form.internal_note.trim() || null,
      };

      if (isLockedChangeOrderStatus(co.status) || isApprovedChangeOrder(co.status)) {
        const payload = buildLockedCommercialUpdate(commercial);
        await supabase.from('jphph_change_orders').update(payload).eq('id', editingId);
        emitChangeOrdersSync(co.project_id);
        await load();
        setEditingId(null);
        setForm(emptyForm);
        setShowFileUploader(false);
        return;
      }

      const duplicateError = await validateCoNumberUnique(co.project_id, form.co_number, editingId);
      if (duplicateError) {
        setFormError(duplicateError);
        return;
      }

      const { co_number, co_sequence } = await resolveCoNumberFields(co.project_id, form.co_number);

      const payload: Record<string, unknown> = {
        title: form.title.trim(),
        co_number,
        co_sequence,
        description: form.description.trim() || null,
        client_description: form.client_description.trim() || null,
        reason: form.reason.trim() || null,
        current_finish_description: form.current_finish_description.trim() || null,
        new_finish_description: form.new_finish_description.trim() || null,
        schedule_impact: form.schedule_impact.trim() || null,
        ...commercial,
        status: form.status,
        requires_client_approval: form.requires_client_approval,
        updated_at: new Date().toISOString(),
        ...buildChangeOrderClientVisibilityPatch(form.status),
      };
      await supabase.from('jphph_change_orders').update(payload).eq('id', editingId);
      emitChangeOrdersSync(co.project_id);
      await load();
      setEditingId(null);
      setForm(emptyForm);
      setShowFileUploader(false);
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to save change order.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteAttachment(file: InstructionAttachment) {
    if (!editingId) return;
    const co = changeOrders.find((c) => c.id === editingId);
    if (!window.confirm(`Remove attachment "${file.file_name}"?`)) return;

    setFormError(null);
    try {
      await unlinkChangeOrderAttachment(file.id);
      await loadAttachmentsForChangeOrder(editingId);
      emitChangeOrdersSync(co?.project_id);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete attachment.';
      setFormError(`Failed to delete attachment: ${message}`);
    }
  }

  function handleAttachmentVisibilityUpdated(
    fileId: string,
    next: { photo_type: string; is_client_visible: boolean }
  ) {
    setAttachments((prev) => {
      const updated: Record<string, InstructionAttachment[]> = {};
      for (const [coId, files] of Object.entries(prev)) {
        updated[coId] = files.map((file) => (file.id === fileId ? { ...file, ...next } : file));
      }
      return updated;
    });
  }

  const getProjectName = (id: string) => {
    const p = projects.find((proj) => proj.id === id);
    return p?.project_name || '—';
  };

  const getProjectClient = (id: string) => {
    const p = projects.find((proj) => proj.id === id);
    return p?.client_name || null;
  };

  const filtered = changeOrders.filter((co) => {
    if (statusFilter === 'archived') return co.status === 'cancelled';
    if (statusFilter === 'no_pricing') return isChangeOrderNoPricing(co) && co.status !== 'cancelled';
    if (statusFilter === 'all') return co.status !== 'cancelled';
    if (statusFilter === 'pending_client') return co.requires_client_approval && isPending(co.status);
    if (statusFilter === 'approved') return co.status === 'approved' || co.status === 'client_approved';
    if (statusFilter === 'rejected') return co.status === 'rejected' || co.status === 'client_rejected';
    if (statusFilter === 'closed') return co.status === 'closed';
    return co.status === statusFilter;
  });
  const editingCo = editingId ? changeOrders.find((co) => co.id === editingId) : null;

  const stats = {
    total: changeOrders.length,
    pendingClient: changeOrders.filter((c) => c.requires_client_approval && isPending(c.status)).length,
    approved: changeOrders.filter((c) => c.status === 'approved' || c.status === 'client_approved').length,
    draft: changeOrders.filter((c) => c.status === 'draft').length,
  };

  async function updateStatus(id: string, newStatus: string) {
    const co = changeOrders.find((c) => c.id === id);
    const visibilityPatch = buildChangeOrderClientVisibilityPatch(newStatus);
    await supabase
      .from('jphph_change_orders')
      .update({ status: newStatus, ...visibilityPatch, updated_at: new Date().toISOString() })
      .eq('id', id);
    setChangeOrders((prev) =>
      prev.map((c) =>
        c.id === id
          ? { ...c, status: newStatus, client_visible: visibilityPatch.client_visible ?? c.client_visible }
          : c
      )
    );
    if (drawerCo?.id === id) {
      setDrawerCo((prev) =>
        prev
          ? { ...prev, status: newStatus, client_visible: visibilityPatch.client_visible ?? prev.client_visible }
          : null
      );
    }
    emitChangeOrdersSync(co?.project_id);
  }

  async function setClientVisibility(id: string, visible: boolean) {
    const co = changeOrders.find((c) => c.id === id);
    await supabase
      .from('jphph_change_orders')
      .update({ client_visible: visible, updated_at: new Date().toISOString() })
      .eq('id', id);
    setChangeOrders((prev) => prev.map((c) => (c.id === id ? { ...c, client_visible: visible } : c)));
    if (drawerCo?.id === id) {
      setDrawerCo((prev) => (prev ? { ...prev, client_visible: visible } : null));
    }
    emitChangeOrdersSync(co?.project_id);
  }

  // Cancelled COs stay visible for audit but never contribute to active financial totals.
  const financialRows = filtered.filter((c) => c.status !== 'cancelled');
  const totalBuilderCost = financialRows.reduce((s, c) => s + (c.builder_cost ?? 0), 0);
  const totalClientPrice = financialRows.reduce((s, c) => s + (c.client_price ?? 0), 0);

  return (
    <MgmtLayout title="Change Orders">
      <div className="px-4 lg:px-6 py-5 w-full overflow-x-hidden">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
          <div>
            <h2 className="text-[#1A1A1A] text-lg font-bold">Change Order Register</h2>
            <p className="text-[#9CA3AF] text-sm mt-0.5">Manage all project change orders — track costs, client approvals, and status</p>
          </div>
          <div className="flex items-center gap-2">
            {/* View toggle */}
            <div className="flex items-center bg-[#F3F4F6] rounded-xl p-1 gap-1">
              <button
                onClick={() => setViewMode('table')}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer transition-colors whitespace-nowrap ${viewMode === 'table' ? 'bg-white text-[#1A1A1A]' : 'text-[#6B7280]'}`}
              >
                <i className="ri-table-line mr-1"></i>Table
              </button>
              <button
                onClick={() => setViewMode('cards')}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer transition-colors whitespace-nowrap ${viewMode === 'cards' ? 'bg-white text-[#1A1A1A]' : 'text-[#6B7280]'}`}
              >
                <i className="ri-layout-masonry-line mr-1"></i>Cards
              </button>
            </div>
            <button
              onClick={load}
              className="flex items-center gap-1.5 bg-white border border-gray-200 text-[#374151] px-3 py-2 rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap"
            >
              <i className="ri-refresh-line"></i>
            </button>
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-1.5 bg-[#E8621A] text-white px-3 py-2 rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line"></i>
              <span className="hidden sm:inline">New Change Order</span>
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
          {[
            { label: 'Total', value: stats.total, color: '#374151', bg: '#F9FAFB' },
            { label: 'Pending Approval', value: stats.pendingClient, color: '#E8621A', bg: '#FEF0E7' },
            { label: 'Approved', value: stats.approved, color: '#059669', bg: '#F0FDF4' },
            { label: 'Draft', value: stats.draft, color: '#9CA3AF', bg: '#F3F4F6' },
          ].map((s) => (
            <div key={s.label} className="rounded-2xl border border-gray-100 p-4" style={{ backgroundColor: s.bg }}>
              <div className="text-2xl font-bold" style={{ color: s.color }}>{s.value}</div>
              <div className="text-xs mt-0.5" style={{ color: s.color }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Financial totals bar */}
        {filtered.length > 0 && (totalBuilderCost !== 0 || totalClientPrice !== 0) && (
          <div className="bg-[#FEF9C3]/60 border border-[#F59E0B]/20 rounded-2xl px-4 py-3 flex flex-wrap gap-4 items-center mb-4">
            <div className="flex items-center gap-1.5">
              <i className="ri-eye-off-line text-[#92400E] text-sm"></i>
              <span className="text-[10px] text-[#9CA3AF]">Builder Cost (internal)</span>
              <span className="text-sm font-bold text-[#1A1A1A]">{formatSignedCurrency(totalBuilderCost)}</span>
            </div>
            <div className="w-px h-4 bg-gray-200"></div>
            <div className="flex items-center gap-1.5">
              <i className="ri-eye-line text-[#059669] text-sm"></i>
              <span className="text-[10px] text-[#9CA3AF]">Client Price (visible)</span>
              <span className="text-sm font-bold text-[#1A1A1A]">{formatSignedCurrency(totalClientPrice)}</span>
            </div>
          </div>
        )}

        {/* Pending alert */}
        {stats.pendingClient > 0 && (
          <div className="bg-[#FEF0E7] border border-[#FDBA74] rounded-2xl p-4 flex items-center gap-3 mb-4">
            <div className="w-9 h-9 rounded-lg bg-[#E8621A] flex items-center justify-center flex-shrink-0">
              <i className="ri-alarm-warning-line text-white text-base"></i>
            </div>
            <div className="flex-1">
              <p className="text-[#E8621A] font-bold text-sm">{stats.pendingClient} change order{stats.pendingClient !== 1 ? 's' : ''} awaiting client approval</p>
              <p className="text-[#374151] text-xs">Client has not yet responded. Follow up or mark approved manually.</p>
            </div>
            <button
              onClick={() => setStatusFilter('pending_client')}
              className="text-xs font-bold text-[#E8621A] bg-white px-3 py-1.5 rounded-lg cursor-pointer whitespace-nowrap border border-[#E8621A]/20"
            >
              View
            </button>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-col gap-2 mb-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <select
              value={selectedProjectId}
              onChange={(e) => setSelectedProjectId(e.target.value)}
              className="w-full bg-white border border-gray-200 rounded-xl px-3 py-2 text-sm text-[#374151] cursor-pointer"
            >
              <option value="all">All Projects</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>{p.project_name}</option>
              ))}
            </select>
          </div>
          <div className="w-full overflow-x-auto scrollbar-hide">
            <div className="flex flex-nowrap gap-1.5 min-w-max">
              {[
                { key: 'all', label: 'All' },
                { key: 'pending_client', label: 'Pending Approval' },
                { key: 'approved', label: 'Approved' },
                { key: 'rejected', label: 'Rejected' },
                { key: 'draft', label: 'Draft' },
                { key: 'closed', label: 'Closed' },
                { key: 'in_progress', label: 'In Progress' },
                { key: 'no_pricing', label: t('co.filter.noPricing') },
                { key: 'archived', label: t('co.filter.archived') },
              ].map((f) => (
                <button
                  key={f.key}
                  onClick={() => setStatusFilter(f.key)}
                  className={`flex-shrink-0 px-3 py-2 rounded-full text-xs font-medium cursor-pointer whitespace-nowrap transition-colors min-h-[36px] ${
                    statusFilter === f.key ? 'bg-[#1A1A1A] text-white' : 'bg-white border border-gray-200 text-[#6B7280]'
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {actionMessage && (
          <div className={`rounded-xl px-3 py-2 text-xs font-medium flex items-center justify-between gap-2 mb-4 ${actionMessage.type === 'success' ? 'bg-[#F0FDF4] text-[#059669] border border-[#86EFAC]/40' : 'bg-[#FEF2F2] text-[#EF4444] border border-[#FECACA]/40'}`}>
            <span>{actionMessage.text}</span>
            <button onClick={() => setActionMessage(null)} className="text-current opacity-70 hover:opacity-100">
              <i className="ri-close-line"></i>
            </button>
          </div>
        )}

        {refreshing && (
          <div className="flex-shrink-0 bg-[#FEF0E7]/60 border border-[#FDBA74]/30 rounded-xl px-3 py-1.5 flex items-center justify-center gap-2 mb-4">
            <i className="ri-loader-4-line animate-spin text-[#E8621A] text-xs"></i>
            <span className="text-[#92400E] text-[11px] font-medium">Refreshing...</span>
          </div>
        )}

        {loading && !hasLoadedOnceRef.current ? (
          <div className="flex items-center justify-center py-16">
            <i className="ri-loader-4-line animate-spin text-[#E8621A] text-2xl"></i>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl border border-gray-100">
            <div className="w-14 h-14 flex items-center justify-center mx-auto mb-3 text-[#D1D5DB]">
              <i className="ri-exchange-line text-5xl"></i>
            </div>
            <p className="text-[#374151] font-semibold mb-1">No change orders found</p>
            <p className="text-[#9CA3AF] text-sm mb-4">Create your first change order to get started</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center gap-1.5 bg-[#E8621A] text-white px-4 py-2.5 rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line"></i>New Change Order
            </button>
          </div>
        ) : viewMode === 'table' ? (
          /* ── TABLE VIEW ── */
          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            {/* Table header */}
            <div className="hidden lg:grid grid-cols-[80px_1fr_160px_120px_100px_100px_100px_90px] gap-x-3 px-4 py-3 border-b border-gray-100 bg-[#F9FAFB]">
              {['CO #', 'Title', 'Project', 'Client', 'Status', 'Builder Cost', 'Client Price', 'Actions'].map((h) => (
                <div key={h} className="text-[10px] font-bold text-[#9CA3AF] uppercase tracking-wider whitespace-nowrap">{h}</div>
              ))}
            </div>

            <div className="divide-y divide-gray-50">
              {filtered.map((co) => {
                const st = getStatusStyle(co.status);
                const archived = isChangeOrderArchived(co.status);
                const isEditing = editingId === co.id;
                const noPricing = isChangeOrderNoPricing(co);
                const projectName = getProjectName(co.project_id);
                const clientName = getProjectClient(co.project_id);
                const pricing = formatInternalPricing(co.builder_cost, co.client_price, {
                  title: co.title,
                  reason: co.reason,
                });
                const margin = pricing.marginValue;
                const clientPortalState = getChangeOrderClientPortalState(co);

                return (
                  <div key={co.id}>
                    {/* Table row — desktop */}
                    <div
                      className="hidden lg:grid grid-cols-[80px_1fr_160px_120px_100px_100px_100px_90px] gap-x-3 px-4 py-3.5 items-center hover:bg-[#FAFAFA] transition-colors"
                    >
                      {/* CO # */}
                      <div>
                        {co.co_number ? (
                          <span className="text-xs font-bold text-[#E8621A] bg-[#FEF0E7] px-2 py-1 rounded-lg tracking-wide whitespace-nowrap">{co.co_number}</span>
                        ) : (
                          <span className="text-xs text-[#9CA3AF]">—</span>
                        )}
                      </div>
                      {/* Title */}
                      <div className="min-w-0">
                        <button
                          onClick={() => setDrawerCo(co)}
                          className="text-sm font-semibold text-[#1A1A1A] hover:text-[#E8621A] cursor-pointer text-left truncate w-full transition-colors"
                          title={co.title}
                        >
                          {co.title}
                        </button>
                        {co.reason && (
                          <p className="text-[10px] text-[#9CA3AF] truncate mt-0.5">{co.reason}</p>
                        )}
                        {noPricing && !archived && (
                          <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-[#F3F4F6] text-[#6B7280] inline-block mt-0.5">
                            {t('co.badge.noPricing')}
                          </span>
                        )}
                      </div>
                      {/* Project */}
                      <div className="min-w-0">
                        <button
                          onClick={() => navigate(`/management/projects/${co.project_id}?tab=Change Orders`)}
                          className="text-xs text-[#374151] hover:text-[#E8621A] cursor-pointer truncate w-full text-left transition-colors font-medium"
                          title={projectName}
                        >
                          {projectName}
                        </button>
                      </div>
                      {/* Client */}
                      <div className="min-w-0">
                        {clientName ? (
                          <span className="text-xs text-[#374151] truncate block">{clientName}</span>
                        ) : (
                          <span className="text-[10px] text-[#D1D5DB] italic">No client</span>
                        )}
                      </div>
                      {/* Status */}
                      <div className="space-y-1">
                        <span
                          className="text-[10px] font-bold px-2 py-1 rounded-full whitespace-nowrap"
                          style={{ backgroundColor: st.bg, color: st.text }}
                        >
                          {st.label}
                        </span>
                        {clientPortalState === 'visible' || clientPortalState === 'auto_visible' ? (
                          <span className="text-[9px] font-semibold text-[#059669] flex items-center gap-0.5">
                            <i className="ri-eye-line"></i> Client
                          </span>
                        ) : clientPortalState === 'hidden' ? (
                          <span className="text-[9px] font-semibold text-[#EF4444] flex items-center gap-0.5">
                            <i className="ri-eye-off-line"></i> Hidden
                          </span>
                        ) : (
                          <span className="text-[9px] font-semibold text-[#9CA3AF] flex items-center gap-0.5">
                            <i className="ri-eye-off-line"></i> Draft
                          </span>
                        )}
                      </div>
                      {/* Builder Cost — internal only */}
                      <div>
                        {co.builder_cost != null ? (
                          <div className="flex items-center gap-1" title="Internal only — not visible to client">
                            <i className="ri-eye-off-line text-[#D1D5DB] text-[10px]"></i>
                            <span className="text-xs font-semibold text-[#1A1A1A]">{pricing.builderText}</span>
                          </div>
                        ) : (
                          <span className="text-[10px] text-[#D1D5DB]">—</span>
                        )}
                      </div>
                      {/* Client Price — client visible */}
                      <div>
                        {co.client_price != null ? (
                          <div className="flex items-center gap-1" title={pricing.isCredit ? 'Client credit' : 'Client-visible price'}>
                            <i className="ri-eye-line text-[#059669] text-[10px]"></i>
                            <span className={`text-xs font-semibold ${pricing.isCredit ? 'text-[#7C3AED]' : 'text-[#059669]'}`}>{pricing.clientText}</span>
                          </div>
                        ) : (
                          <span className="text-[10px] text-[#D1D5DB]">—</span>
                        )}
                      </div>
                      {/* Actions */}
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                          onClick={() => setDrawerCo(co)}
                          className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer"
                          title="View details"
                        >
                          <i className="ri-eye-line text-[#9CA3AF] text-sm"></i>
                        </button>
                        {!archived ? (
                          <button
                            onClick={() => openEdit(co)}
                            className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer"
                            title="Edit"
                          >
                            <i className="ri-edit-line text-[#9CA3AF] text-sm"></i>
                          </button>
                        ) : (
                          <div className="w-7 h-7 flex items-center justify-center rounded-lg" title="Archived">
                            <i className="ri-archive-line text-[#D1D5DB] text-sm"></i>
                          </div>
                        )}
                        {!archived && (
                          <button
                            onClick={() => setConfirmDeleteId(co.id)}
                            className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-[#FEF2F2] cursor-pointer"
                            title={t('co.action.delete')}
                          >
                            <i className="ri-delete-bin-line text-[#EF4444] text-sm"></i>
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Mobile card view (used in table mode on small screens) */}
                    <div className="lg:hidden p-4">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          {co.co_number && (
                            <span className="text-xs font-bold text-[#E8621A] bg-[#FEF0E7] px-2 py-0.5 rounded-lg">{co.co_number}</span>
                          )}
                          <h4 className="text-sm font-semibold text-[#1A1A1A]">{co.title}</h4>
                        </div>
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap flex-shrink-0" style={{ backgroundColor: st.bg, color: st.text }}>
                          {st.label}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-[#6B7280] mb-2">
                        <span className="font-medium">{projectName}</span>
                        {clientName && <><span>·</span><span>{clientName}</span></>}
                      </div>
                      {/* Costs on mobile */}
                      <div className="flex gap-4 text-xs mb-2">
                        {co.builder_cost != null && (
                          <span className="text-[#6B7280]">
                            <i className="ri-eye-off-line mr-0.5 text-[10px]"></i>{pricing.builderLabel}: <span className="font-semibold text-[#1A1A1A]">{pricing.builderText}</span>
                          </span>
                        )}
                        {co.client_price != null && (
                          <span className={pricing.isCredit ? 'text-[#7C3AED]' : 'text-[#059669]'}>
                            <i className="ri-eye-line mr-0.5 text-[10px]"></i>{pricing.clientLabel}: <span className="font-semibold">{pricing.clientText}</span>
                          </span>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => setDrawerCo(co)} className="flex-1 py-1.5 rounded-lg bg-gray-100 text-[#374151] text-xs font-semibold cursor-pointer whitespace-nowrap text-center">View</button>
                        {!archived && (
                          <button onClick={() => openEdit(co)} className="flex-1 py-1.5 rounded-lg bg-[#FEF0E7] text-[#E8621A] text-xs font-semibold cursor-pointer whitespace-nowrap text-center">Edit</button>
                        )}
                      </div>
                    </div>

                    {/* Inline Edit form */}
                    {isEditing && !archived && (
                      <EditForm
                        form={form}
                        setForm={setForm}
                        saving={saving}
                        onSave={handleSaveEdit}
                        onCancel={cancelEdit}
                        coId={co.id}
                        formError={formError}
                        attachments={attachments[co.id] || []}
                        attachmentLoading={attachmentLoadingIds.has(co.id)}
                        attachmentError={attachmentErrors[co.id]}
                        onRetryAttachments={() => void loadAttachmentsForChangeOrder(co.id)}
                        onAddAttachment={() => setShowFileUploader(true)}
                        onDeleteAttachment={(file) => void handleDeleteAttachment(file)}
                        onVisibilityUpdated={handleAttachmentVisibilityUpdated}
                      />
                    )}
                  </div>
                );
              })}
            </div>

            {/* Table footer totals */}
            {(totalBuilderCost !== 0 || totalClientPrice !== 0) && (
              <div className="hidden lg:grid grid-cols-[80px_1fr_160px_120px_100px_100px_100px_90px] gap-x-3 px-4 py-3 bg-[#F9FAFB] border-t border-gray-100">
                <div></div>
                <div className="text-xs font-bold text-[#374151]">Totals ({filtered.length} items)</div>
                <div></div>
                <div></div>
                <div></div>
                <div>
                  <div className="flex items-center gap-1">
                    <i className="ri-eye-off-line text-[#D1D5DB] text-[10px]"></i>
                    <span className="text-xs font-bold text-[#1A1A1A]">{formatSignedCurrency(totalBuilderCost)}</span>
                  </div>
                </div>
                <div>
                  <div className="flex items-center gap-1">
                    <i className="ri-eye-line text-[#059669] text-[10px]"></i>
                    <span className="text-xs font-bold text-[#059669]">{formatSignedCurrency(totalClientPrice)}</span>
                  </div>
                </div>
                <div></div>
              </div>
            )}
          </div>
        ) : (
          /* ── CARD VIEW ── */
          <div className="space-y-3">
            {filtered.map((co) => {
              const st = getStatusStyle(co.status);
              const archived = isChangeOrderArchived(co.status);
              const isEditing = editingId === co.id;
              const noPricing = isChangeOrderNoPricing(co);
              const isPendingApproval = co.requires_client_approval && isPending(co.status);
              const projectName = getProjectName(co.project_id);
              const clientName = getProjectClient(co.project_id);
              const pricing = formatInternalPricing(co.builder_cost, co.client_price, {
                title: co.title,
                reason: co.reason,
              });
              const margin = pricing.marginValue;
              const coAttachments = attachments[co.id] || [];

              return (
                <div key={co.id} className={`bg-white rounded-2xl border overflow-hidden ${isPendingApproval ? 'border-[#E8621A]/30' : 'border-gray-100'}`}>
                  {isPendingApproval && <div className="h-1 bg-[#E8621A]"></div>}
                  <div className="p-4">
                    {/* Header row */}
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-0.5">
                          {co.co_number && (
                            <span className="text-xs font-bold text-[#E8621A] bg-[#FEF0E7] px-2 py-0.5 rounded-lg tracking-wide flex-shrink-0">
                              {co.co_number}
                            </span>
                          )}
                          {noPricing && !archived && (
                            <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-[#F3F4F6] text-[#6B7280] flex-shrink-0">
                              {t('co.badge.noPricing')}
                            </span>
                          )}
                          <h4 className="text-sm font-semibold text-[#1A1A1A] leading-snug">{co.title}</h4>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-[#9CA3AF] mt-0.5">
                          <button
                            onClick={() => navigate(`/management/projects/${co.project_id}?tab=Change Orders`)}
                            className="font-medium text-[#374151] hover:text-[#E8621A] cursor-pointer transition-colors"
                          >
                            {projectName}
                          </button>
                          {clientName && (
                            <>
                              <span>·</span>
                              <span>{clientName}</span>
                            </>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap" style={{ backgroundColor: st.bg, color: st.text }}>
                          {st.label}
                        </span>
                        <button onClick={() => setDrawerCo(co)} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer" title="View">
                          <i className="ri-eye-line text-[#9CA3AF] text-sm"></i>
                        </button>
                        {!archived ? (
                          <button onClick={() => openEdit(co)} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer" title="Edit">
                            <i className="ri-edit-line text-[#9CA3AF] text-sm"></i>
                          </button>
                        ) : (
                          <div className="w-7 h-7 flex items-center justify-center rounded-lg" title="Archived">
                            <i className="ri-archive-line text-[#D1D5DB] text-sm"></i>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Cost row — clearly labeled */}
                    {(co.builder_cost != null || co.client_price != null) && (
                      <div className="flex gap-4 flex-wrap mb-2">
                        {co.builder_cost != null && (
                          <div className="flex items-center gap-1.5 bg-[#FEF9C3]/60 px-2.5 py-1.5 rounded-lg border border-[#F59E0B]/20">
                            <i className="ri-eye-off-line text-[#92400E] text-[10px]"></i>
                            <div>
                              <p className="text-[9px] text-[#92400E] font-bold uppercase tracking-wide">{pricing.builderLabel}</p>
                              <p className="text-xs font-bold text-[#1A1A1A]">{pricing.builderText}</p>
                            </div>
                          </div>
                        )}
                        {co.client_price != null && (
                          <div className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border ${pricing.isCredit ? 'bg-[#F5F3FF] border-[#DDD6FE]' : 'bg-[#F0FDF4] border-[#BBF7D0]'}`}>
                            <i className="ri-eye-line text-[#059669] text-[10px]"></i>
                            <div>
                              <p className={`text-[9px] font-bold uppercase tracking-wide ${pricing.isCredit ? 'text-[#7C3AED]' : 'text-[#059669]'}`}>{pricing.clientLabel}</p>
                              <p className={`text-xs font-bold ${pricing.isCredit ? 'text-[#7C3AED]' : 'text-[#059669]'}`}>{pricing.clientText}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Client description preview */}
                    {co.client_description && (
                      <div className="mb-2">
                        <p className="text-[10px] font-bold text-[#059669] uppercase tracking-wider mb-1">
                          <i className="ri-eye-line mr-1"></i>Client Description
                        </p>
                        <p className="text-[#6B7280] text-sm leading-relaxed line-clamp-2">{co.client_description}</p>
                      </div>
                    )}

                    {/* Internal note preview */}
                    {co.internal_note && (
                      <div className="mb-2 bg-[#FEF9C3]/40 rounded-lg px-2.5 py-1.5 border border-[#F59E0B]/10">
                        <p className="text-[10px] font-bold text-[#92400E] uppercase tracking-wider mb-0.5">
                          <i className="ri-eye-off-line mr-1"></i>Internal Note
                        </p>
                        <p className="text-[#6B7280] text-xs italic line-clamp-2">{co.internal_note}</p>
                      </div>
                    )}

                    {coAttachments.length > 0 && (
                      <div className="mt-2 mb-2">
                        <InstructionAttachmentThumbnails files={coAttachments} maxVisible={3} />
                      </div>
                    )}

                    {!archived && (
                      <div className="mt-3 pt-3 border-t border-gray-50 flex items-center gap-2 flex-wrap">
                        {co.status === 'draft' && (
                          <button onClick={() => updateStatus(co.id, 'pending_client_approval')} className="px-3 py-1.5 rounded-lg bg-[#FEF0E7] text-[#E8621A] text-xs font-bold cursor-pointer whitespace-nowrap hover:bg-[#E8621A]/10">
                            <i className="ri-send-plane-line mr-0.5"></i>Send to Client
                          </button>
                        )}
                        {isPending(co.status) && (
                          <>
                            <button onClick={() => updateStatus(co.id, 'approved')} className="px-3 py-1.5 rounded-lg bg-[#F0FDF4] text-[#059669] text-xs font-bold cursor-pointer whitespace-nowrap hover:bg-[#059669]/10">
                              <i className="ri-check-line mr-0.5"></i>Mark Approved
                            </button>
                            <button onClick={() => updateStatus(co.id, 'rejected')} className="px-3 py-1.5 rounded-lg bg-[#FEF2F2] text-[#EF4444] text-xs font-bold cursor-pointer whitespace-nowrap hover:bg-[#EF4444]/10">
                              <i className="ri-close-line mr-0.5"></i>Mark Rejected
                            </button>
                          </>
                        )}
                        {(co.status === 'approved' || co.status === 'client_approved' || co.status === 'in_progress') && (
                          <button onClick={() => updateStatus(co.id, 'closed')} className="px-3 py-1.5 rounded-lg bg-gray-100 text-[#6B7280] text-xs font-bold cursor-pointer whitespace-nowrap hover:bg-gray-200">
                            <i className="ri-close-circle-line mr-0.5"></i>Close
                          </button>
                        )}
                        {co.status === 'rejected' && (
                          <button onClick={() => updateStatus(co.id, 'draft')} className="px-3 py-1.5 rounded-lg bg-gray-100 text-[#374151] text-xs font-bold cursor-pointer whitespace-nowrap hover:bg-gray-200">
                            <i className="ri-arrow-go-back-line mr-0.5"></i>Revert to Draft
                          </button>
                        )}
                        <button onClick={() => setConfirmDeleteId(co.id)} className="px-3 py-1.5 rounded-lg bg-[#FEF2F2] text-[#EF4444] text-xs font-bold cursor-pointer whitespace-nowrap hover:bg-[#FECACA]/40">
                          <i className="ri-delete-bin-line mr-0.5"></i>
                          {t('co.action.delete')}
                        </button>
                        <span className="text-[#9CA3AF] text-[10px] ml-auto">{formatDateTime(co.created_at)}</span>
                      </div>
                    )}

                    {/* Inline edit form in card mode */}
                    {isEditing && !archived && (
                      <EditForm
                        form={form}
                        setForm={setForm}
                        saving={saving}
                        onSave={handleSaveEdit}
                        onCancel={cancelEdit}
                        coId={co.id}
                        formError={formError}
                        attachments={attachments[co.id] || []}
                        attachmentLoading={attachmentLoadingIds.has(co.id)}
                        attachmentError={attachmentErrors[co.id]}
                        onRetryAttachments={() => void loadAttachmentsForChangeOrder(co.id)}
                        onAddAttachment={() => setShowFileUploader(true)}
                        onDeleteAttachment={(file) => void handleDeleteAttachment(file)}
                        onVisibilityUpdated={handleAttachmentVisibilityUpdated}
                      />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Detail Drawer */}
      {drawerCo && (
        <CoDetailDrawer
          co={drawerCo}
          projectName={getProjectName(drawerCo.project_id)}
          clientName={getProjectClient(drawerCo.project_id)}
          attachments={attachments[drawerCo.id] || []}
          onClose={() => setDrawerCo(null)}
          onEdit={() => { openEdit(drawerCo); }}
          onEditCoNumber={() => { setCoNumberEditCo(drawerCo); setCoNumberEditError(null); }}
          onStatusChange={updateStatus}
          onNavigate={() => navigate(`/management/projects/${drawerCo.project_id}?tab=Change Orders`)}
          onDeleteRequest={() => setConfirmDeleteId(drawerCo.id)}
        />
      )}

      {confirmDeleteId && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setConfirmDeleteId(null)}></div>
          <div className="relative bg-white rounded-2xl p-5 max-w-sm w-full shadow-xl">
            <h4 className="text-sm font-bold text-[#1A1A1A] mb-2">{t('co.confirmDeleteTitle')}</h4>
            <p className="text-xs text-[#6B7280] mb-4">
              {(() => {
                const co = changeOrders.find((c) => c.id === confirmDeleteId);
                if (co && requiresDeleteStrongWarning(co)) return t('co.confirmDeleteStrongWarning');
                return t('co.confirmDeleteDefault');
              })()}
            </p>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setConfirmDeleteId(null)}
                className="px-4 py-2 rounded-xl border border-gray-200 text-[#374151] text-xs font-semibold cursor-pointer"
              >
                {t('co.action.cancelConfirm')}
              </button>
              <button
                onClick={() => {
                  const co = changeOrders.find((c) => c.id === confirmDeleteId);
                  if (co) void handleDeleteChangeOrder(co);
                }}
                className="px-4 py-2 rounded-xl bg-[#EF4444] text-white text-xs font-semibold cursor-pointer"
              >
                {t('co.action.delete')}
              </button>
            </div>
          </div>
        </div>
      )}

      {showEditModal && editCo && (
        <EditChangeOrderModal
          show={showEditModal}
          co={{
            ...editCo,
            invoice_status: editCo.invoice_status ?? null,
            invoice_url: editCo.invoice_url ?? null,
            invoice_file_id: editCo.invoice_file_id ?? null,
            invoiced_at: editCo.invoiced_at ?? null,
            paid_at: editCo.paid_at ?? null,
          }}
          attachments={attachments[editCo.id] || []}
          invoiceFieldsAvailable={invoiceFieldsAvailable}
          onClose={() => { setShowEditModal(false); setEditCo(null); }}
          onSaved={() => {
            void load();
            setShowEditModal(false);
            setEditCo(null);
          }}
        />
      )}

      <CoNumberQuickEditModal
        open={!!coNumberEditCo}
        initialCoNumber={coNumberEditCo?.co_number || ''}
        saving={coNumberSaving}
        error={coNumberEditError}
        onClose={() => { setCoNumberEditCo(null); setCoNumberEditError(null); }}
        onSave={(coNumber) => void handleSaveCoNumber(coNumber)}
      />

      {showCreateModal && (
        <NewChangeOrderModal
          show={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          projectId={selectedProjectId !== 'all' ? selectedProjectId : ''}
          allowProjectChange={true}
          source="sidebar-change-orders"
          onSuccess={() => { setShowCreateModal(false); load(); }}
        />
      )}

      {showFileUploader && user && editingId && editingCo && (
        <FileUploader
          projectId={editingCo.project_id}
          currentUserId={user.id}
          open={showFileUploader}
          onClose={() => setShowFileUploader(false)}
          onUploadComplete={(records) => {
            console.warn('[ChangeOrders] uploaded edit attachments', {
              change_order_id: editingId,
              count: records.length,
            });
            setShowFileUploader(false);
            void loadAttachmentsForChangeOrder(editingId);
          }}
          accountType="management"
          category="change_order"
          allowMultiple
          allowCamera
          allowPdf
          enablePerFileCaption
          showVisibilitySelector={true}
          visibilityScope="management"
          bucket="change-order-photos"
          relatedType="change_order"
          relatedId={editingId}
          title="Attach Change Order Files"
        />
      )}
    </MgmtLayout>
  );
}

/* ─── Edit Form component ─── */
interface EditFormProps {
  form: {
    co_number: string;
    title: string; description: string; client_description: string; reason: string;
    current_finish_description: string; new_finish_description: string; schedule_impact: string;
    builder_cost: string; client_price: string; internal_note: string; status: string;
    requires_client_approval: boolean;
  };
  setForm: React.Dispatch<React.SetStateAction<EditFormProps['form']>>;
  saving: boolean;
  onSave: () => void;
  onCancel: () => void;
  coId: string;
  formError?: string | null;
  attachments: InstructionAttachment[];
  attachmentLoading: boolean;
  attachmentError?: string;
  onRetryAttachments: () => void;
  onAddAttachment: () => void;
  onDeleteAttachment: (file: InstructionAttachment) => void;
  onVisibilityUpdated: (fileId: string, next: { photo_type: string; is_client_visible: boolean }) => void;
}

function EditForm({
  form,
  setForm,
  saving,
  onSave,
  onCancel,
  coId,
  formError,
  attachments,
  attachmentLoading,
  attachmentError,
  onRetryAttachments,
  onAddAttachment,
  onDeleteAttachment,
  onVisibilityUpdated,
}: EditFormProps) {
  return (
    <div className="bg-[#F9FAFB] rounded-xl p-4 border border-gray-100 mt-3 space-y-3">
      <div className="flex items-center justify-between">
        <h5 className="text-xs font-bold text-[#1A1A1A]">Edit Change Order</h5>
        <button onClick={onCancel} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-200 cursor-pointer">
          <i className="ri-close-line text-[#6B7280]"></i>
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-semibold text-[#374151] mb-1 block">CO Number *</label>
          <input value={form.co_number} onChange={(e) => setForm((p) => ({ ...p, co_number: e.target.value }))}
            onBlur={() => setForm((p) => ({ ...p, co_number: p.co_number ? normalizeCoNumber(p.co_number) : '' }))}
            placeholder="CO-001"
            className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm text-[#1A1A1A] focus:outline-none focus:border-[#E8621A]" />
        </div>

        <div className="sm:col-span-2">
          <label className="text-xs font-semibold text-[#374151] mb-1 block">Title *</label>
          <input value={form.title} onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
            className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm text-[#1A1A1A] focus:outline-none focus:border-[#E8621A]" />
        </div>

        <div className="sm:col-span-2">
          <label className="text-xs font-semibold text-[#374151] mb-1 block">
            Internal Description <span className="text-[10px] font-normal text-[#9CA3AF]">(internal only)</span>
          </label>
          <textarea value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
            rows={2} className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm text-[#1A1A1A] focus:outline-none focus:border-[#E8621A] resize-none" />
        </div>

        <div className="sm:col-span-2">
          <label className="text-xs font-semibold text-[#374151] mb-1 block">
            Client Description <span className="text-[10px] font-normal text-[#059669]">(client-visible)</span>
          </label>
          <textarea value={form.client_description} onChange={(e) => setForm((p) => ({ ...p, client_description: e.target.value }))}
            rows={2} className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm text-[#1A1A1A] focus:outline-none focus:border-[#E8621A] resize-none" />
        </div>

        <div className="sm:col-span-2">
          <label className="text-xs font-semibold text-[#374151] mb-1 block">Reason for Change</label>
          <input value={form.reason} onChange={(e) => setForm((p) => ({ ...p, reason: e.target.value }))}
            className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm text-[#1A1A1A] focus:outline-none focus:border-[#E8621A]" />
        </div>

        <div>
          <label className="text-xs font-semibold text-[#374151] mb-1 block">Current Finish</label>
          <input value={form.current_finish_description} onChange={(e) => setForm((p) => ({ ...p, current_finish_description: e.target.value }))}
            className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm text-[#1A1A1A] focus:outline-none focus:border-[#E8621A]" />
        </div>
        <div>
          <label className="text-xs font-semibold text-[#374151] mb-1 block">New Finish</label>
          <input value={form.new_finish_description} onChange={(e) => setForm((p) => ({ ...p, new_finish_description: e.target.value }))}
            className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm text-[#1A1A1A] focus:outline-none focus:border-[#E8621A]" />
        </div>

        <div className="sm:col-span-2">
          <label className="text-xs font-semibold text-[#374151] mb-1 block">Schedule Impact</label>
          <input value={form.schedule_impact} onChange={(e) => setForm((p) => ({ ...p, schedule_impact: e.target.value }))}
            className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm text-[#1A1A1A] focus:outline-none focus:border-[#E8621A]" />
        </div>

        <div>
          <label className="text-xs font-semibold text-[#374151] mb-1 block">
            Builder Cost (excl. GST) <span className="text-[10px] font-normal text-[#EF4444]">(internal only)</span>
          </label>
          <input type="text" inputMode="decimal" value={form.builder_cost} onChange={(e) => setForm((p) => ({ ...p, builder_cost: e.target.value }))}
            className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm text-[#1A1A1A] focus:outline-none focus:border-[#E8621A]" />
        </div>
        <div>
          <label className="text-xs font-semibold text-[#374151] mb-1 block">
            Client Price (incl. GST) <span className="text-[10px] font-normal text-[#059669]">(client-visible)</span>
          </label>
          <input type="text" inputMode="decimal" value={form.client_price} onChange={(e) => setForm((p) => ({ ...p, client_price: e.target.value }))}
            className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm text-[#1A1A1A] focus:outline-none focus:border-[#E8621A]" />
        </div>

        <div className="sm:col-span-2">
          <label className="text-xs font-semibold text-[#374151] mb-1 block">
            Internal Note <span className="text-[10px] font-normal text-[#9CA3AF]">(internal only)</span>
          </label>
          <textarea value={form.internal_note} onChange={(e) => setForm((p) => ({ ...p, internal_note: e.target.value }))}
            rows={2} className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm text-[#1A1A1A] focus:outline-none focus:border-[#E8621A] resize-none" />
        </div>

        <div>
          <label className="text-xs font-semibold text-[#374151] mb-1 block">Status</label>
          <select value={form.status} onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))}
            className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm text-[#1A1A1A] focus:outline-none focus:border-[#E8621A] bg-white cursor-pointer">
            {Object.entries(EDITABLE_STATUSES_LABELS).map(([val, lbl]) => (
              <option key={val} value={val}>{lbl}</option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-2">
          <input type="checkbox" id={`rca_${coId}`} checked={form.requires_client_approval}
            onChange={(e) => setForm((p) => ({ ...p, requires_client_approval: e.target.checked }))}
            className="w-4 h-4 accent-[#E8621A] cursor-pointer" />
          <label htmlFor={`rca_${coId}`} className="text-xs text-[#374151] cursor-pointer">Requires Client Approval</label>
        </div>

        <div className="sm:col-span-2 space-y-2 pt-3 border-t border-gray-100">
          <div className="flex items-center justify-between gap-3">
            <h6 className="text-xs font-bold text-[#1A1A1A]">Change Order Attachments</h6>
            <button
              type="button"
              onClick={onAddAttachment}
              className="px-3 py-1.5 rounded-lg border border-gray-200 text-[#374151] text-xs font-semibold hover:border-[#E8621A] hover:text-[#E8621A] cursor-pointer whitespace-nowrap"
            >
              <i className="ri-upload-2-line mr-1"></i>
              Add files
            </button>
          </div>
          <ChangeOrderAttachmentList
            files={attachments}
            loading={attachmentLoading}
            error={attachmentError}
            onRetry={onRetryAttachments}
            onDelete={onDeleteAttachment}
            onVisibilityUpdated={onVisibilityUpdated}
          />
        </div>
      </div>

      {formError && (
        <p className="text-xs text-[#EF4444]">{formError}</p>
      )}

      <div className="flex gap-2 justify-end pt-1">
        <button onClick={onCancel} className="px-4 py-2 rounded-xl border border-gray-200 text-[#374151] text-xs font-semibold cursor-pointer whitespace-nowrap">
          Cancel
        </button>
        <button onClick={onSave} disabled={saving || !form.title.trim() || !form.co_number.trim()}
          className="px-4 py-2 rounded-xl bg-[#E8621A] text-white text-xs font-semibold cursor-pointer whitespace-nowrap disabled:opacity-50">
          {saving ? <i className="ri-loader-4-line animate-spin"></i> : 'Save Changes'}
        </button>
      </div>
    </div>
  );
}

/* ─── Detail Drawer component ─── */
interface DrawerProps {
  co: ChangeOrderRecord;
  projectName: string;
  clientName: string | null;
  attachments: InstructionAttachment[];
  onClose: () => void;
  onEdit: () => void;
  onEditCoNumber: () => void;
  onStatusChange: (id: string, status: string) => void;
  onNavigate: () => void;
  onDeleteRequest: () => void;
}

function CoDetailDrawer({ co, projectName, clientName, attachments, onClose, onEdit, onEditCoNumber, onStatusChange, onNavigate, onDeleteRequest }: DrawerProps) {
  const { t } = useTranslation();
  const archived = isChangeOrderArchived(co.status);
  const noPricing = isChangeOrderNoPricing(co);
  const st = getStatusStyle(co.status);
  const pricing = formatInternalPricing(co.builder_cost, co.client_price, {
    title: co.title,
    reason: co.reason,
  });

  return (
    <div className="fixed inset-0 z-[80] flex">
      <div className="absolute inset-0 bg-black/40" onClick={onClose}></div>
      <div className="relative ml-auto w-full max-w-md bg-white h-full overflow-y-auto flex flex-col">
        {/* Drawer header */}
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between flex-shrink-0 sticky top-0 bg-white z-10">
          <div className="flex items-center gap-2">
            {co.co_number && (
              <span className="text-xs font-bold text-[#E8621A] bg-[#FEF0E7] px-2 py-0.5 rounded-lg">{co.co_number}</span>
            )}
            {noPricing && !archived && (
              <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-[#F3F4F6] text-[#6B7280]">{t('co.badge.noPricing')}</span>
            )}
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap" style={{ backgroundColor: st.bg, color: st.text }}>{st.label}</span>
          </div>
          <div className="flex items-center gap-1">
            {!archived && (
              <button onClick={onEdit} className="px-3 py-1.5 rounded-lg bg-[#FEF0E7] text-[#E8621A] text-xs font-semibold cursor-pointer whitespace-nowrap">
                <i className="ri-edit-line mr-1"></i>Edit
              </button>
            )}
            <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 cursor-pointer ml-1">
              <i className="ri-close-line text-[#374151]"></i>
            </button>
          </div>
        </div>

        <div className="flex-1 px-5 py-4 space-y-4">
          <div>
            <h3 className="text-base font-bold text-[#1A1A1A] leading-snug">{co.title}</h3>
            <div className="flex items-center gap-2 mt-1 text-xs text-[#9CA3AF]">
              <button onClick={onNavigate} className="text-[#374151] font-medium hover:text-[#E8621A] cursor-pointer transition-colors">{projectName}</button>
              {clientName && <><span>·</span><span>{clientName}</span></>}
              <span>·</span>
              <span>{new Date(co.created_at).toLocaleDateString()}</span>
            </div>
          </div>

          {/* ── INTERNAL INFORMATION BLOCK ── */}
          <div className="bg-[#FEF9C3]/60 rounded-xl p-4 border border-[#F59E0B]/20 space-y-3">
            <p className="text-[10px] font-bold text-[#92400E] uppercase tracking-wider flex items-center gap-1">
              <i className="ri-eye-off-line"></i>Internal Information — not visible to client
            </p>

            {/* Cost breakdown */}
            {(co.builder_cost != null || co.client_price != null) && (
              <div className="grid grid-cols-3 gap-3">
                {pricing.builderText && (
                  <div>
                    <p className="text-[10px] text-[#9CA3AF]">{pricing.builderLabel}</p>
                    <p className="text-sm font-bold text-[#1A1A1A]">{pricing.builderText}</p>
                  </div>
                )}
                {pricing.clientText && (
                  <div>
                    <p className="text-[10px] text-[#9CA3AF]">{pricing.clientLabel}</p>
                    <p className={`text-sm font-bold ${pricing.isCredit ? 'text-[#7C3AED]' : 'text-[#059669]'}`}>{pricing.clientText}</p>
                  </div>
                )}
                {pricing.marginText && (
                  <div>
                    <p className="text-[10px] text-[#9CA3AF]">{pricing.marginLabel}</p>
                    <p className={`text-sm font-bold ${(pricing.marginValue ?? 0) >= 0 ? 'text-[#059669]' : 'text-[#EF4444]'}`}>
                      {pricing.marginText}
                    </p>
                  </div>
                )}
              </div>
            )}

            {co.description && (
              <div>
                <p className="text-[10px] text-[#9CA3AF] mb-1">Internal Description</p>
                <p className="text-sm text-[#374151] leading-relaxed">{co.description}</p>
              </div>
            )}

            {co.internal_note && (
              <div>
                <p className="text-[10px] text-[#9CA3AF] mb-1">Internal Note</p>
                <p className="text-sm text-[#6B7280] italic">{co.internal_note}</p>
              </div>
            )}
          </div>

          {/* ── CLIENT-VISIBLE BLOCK ── */}
          <div className="bg-[#F0FDF4] rounded-xl p-4 border border-[#BBF7D0] space-y-3">
            <p className="text-[10px] font-bold text-[#059669] uppercase tracking-wider flex items-center gap-1">
              <i className="ri-eye-line"></i>Client-Visible Information
            </p>

            {co.client_description && (
              <div>
                <p className="text-[10px] text-[#9CA3AF] mb-1">Client Description</p>
                <p className="text-sm text-[#374151] leading-relaxed">{co.client_description}</p>
              </div>
            )}

            {co.client_price != null && (() => {
              const price = formatClientFacingPrice(co.client_price, {
                title: co.title,
                reason: co.reason,
              });
              return (
              <div>
                <p className="text-[10px] text-[#9CA3AF] mb-0.5">{price.isCredit ? 'Client Credit (incl. GST)' : 'Client Price (incl. GST)'}</p>
                <p className={`text-lg font-bold ${price.isCredit ? 'text-[#7C3AED]' : 'text-[#059669]'}`}>{price.amountText}</p>
              </div>
              );
            })()}

            {(co.current_finish_description || co.new_finish_description) && (
              <div className="grid grid-cols-2 gap-3">
                {co.current_finish_description && (
                  <div>
                    <p className="text-[10px] text-[#9CA3AF] mb-0.5">Current Finish</p>
                    <p className="text-xs text-[#374151]">{co.current_finish_description}</p>
                  </div>
                )}
                {co.new_finish_description && (
                  <div>
                    <p className="text-[10px] text-[#9CA3AF] mb-0.5">New Finish</p>
                    <p className="text-xs text-[#374151]">{co.new_finish_description}</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ── CHANGE DETAILS ── */}
          {(co.reason || co.schedule_impact || co.cost_impact) && (
            <div className="bg-[#F5F3FF]/50 rounded-xl p-4 border border-[#7C3AED]/10 space-y-2">
              <p className="text-[10px] font-bold text-[#7C3AED] uppercase tracking-wider">Change Details</p>
              {co.reason && (
                <div>
                  <p className="text-[10px] text-[#9CA3AF]">Reason for Change</p>
                  <p className="text-sm text-[#374151]">{co.reason}</p>
                </div>
              )}
              {co.schedule_impact && (
                <div>
                  <p className="text-[10px] text-[#9CA3AF]">Schedule Impact</p>
                  <p className="text-sm text-[#374151]">{co.schedule_impact}</p>
                </div>
              )}
            </div>
          )}

          {/* Client response */}
          {(co.client_decision || co.client_comment) && (
            <div className="bg-[#F9FAFB] rounded-xl p-4 border border-gray-100 space-y-1">
              <p className="text-[10px] font-bold text-[#9CA3AF] uppercase tracking-wider">Client Response</p>
              {co.client_decision && (
                <p className="text-xs font-semibold capitalize" style={{ color: co.client_decision === 'approved' ? '#059669' : '#EF4444' }}>
                  {co.client_decision}
                </p>
              )}
              {co.client_comment && <p className="text-xs text-[#374151] italic">{co.client_comment}</p>}
              {co.client_decided_at && (
                <p className="text-[10px] text-[#9CA3AF]">{new Date(co.client_decided_at).toLocaleDateString()}</p>
              )}
            </div>
          )}

          {/* Attachments */}
          {attachments.length > 0 && (
            <div>
              <p className="text-[10px] font-bold text-[#9CA3AF] uppercase tracking-wider mb-2">Attachments</p>
              <InstructionAttachmentThumbnails files={attachments} maxVisible={6} />
            </div>
          )}

          {!archived && (
            <div className="pt-2 space-y-2">
              <p className="text-[10px] font-bold text-[#9CA3AF] uppercase tracking-wider">Actions</p>
              <div className="flex flex-wrap gap-2">
                {co.status === 'draft' && (
                  <button onClick={() => { onStatusChange(co.id, 'pending_client_approval'); onClose(); }}
                    className="flex-1 py-2.5 rounded-xl bg-[#FEF0E7] text-[#E8621A] text-xs font-bold cursor-pointer whitespace-nowrap text-center border border-[#E8621A]/20">
                    <i className="ri-send-plane-line mr-1"></i>Send to Client
                  </button>
                )}
                {isPending(co.status) && (
                  <>
                    <button onClick={() => { onStatusChange(co.id, 'approved'); onClose(); }}
                      className="flex-1 py-2.5 rounded-xl bg-[#F0FDF4] text-[#059669] text-xs font-bold cursor-pointer whitespace-nowrap text-center border border-[#BBF7D0]">
                      <i className="ri-check-line mr-1"></i>Mark Approved
                    </button>
                    <button onClick={() => { onStatusChange(co.id, 'rejected'); onClose(); }}
                      className="flex-1 py-2.5 rounded-xl bg-[#FEF2F2] text-[#EF4444] text-xs font-bold cursor-pointer whitespace-nowrap text-center border border-[#FECACA]">
                      <i className="ri-close-line mr-1"></i>Mark Rejected
                    </button>
                  </>
                )}
                {(co.status === 'approved' || co.status === 'client_approved' || co.status === 'in_progress') && (
                  <button onClick={() => { onStatusChange(co.id, 'closed'); onClose(); }}
                    className="flex-1 py-2.5 rounded-xl bg-gray-100 text-[#6B7280] text-xs font-bold cursor-pointer whitespace-nowrap text-center">
                    <i className="ri-close-circle-line mr-1"></i>Close CO
                  </button>
                )}
                {co.status === 'rejected' && (
                  <button onClick={() => { onStatusChange(co.id, 'draft'); onClose(); }}
                    className="flex-1 py-2.5 rounded-xl bg-gray-100 text-[#374151] text-xs font-bold cursor-pointer whitespace-nowrap text-center">
                    <i className="ri-arrow-go-back-line mr-1"></i>Revert to Draft
                  </button>
                )}
                <button
                  onClick={onDeleteRequest}
                  className="flex-1 py-2.5 rounded-xl bg-[#FEF2F2] text-[#EF4444] text-xs font-bold cursor-pointer whitespace-nowrap text-center"
                >
                  <i className="ri-delete-bin-line mr-1"></i>
                  {t('co.action.delete')}
                </button>
              </div>
              <button onClick={onNavigate}
                className="w-full py-2.5 rounded-xl bg-[#F9FAFB] text-[#374151] text-xs font-semibold cursor-pointer whitespace-nowrap text-center border border-gray-100">
                <i className="ri-external-link-line mr-1"></i>View in Project
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
